'use client';

import { ComponentProps, useContext } from 'react';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { isValidSocialHref } from './has-social-links';

type Props = ComponentProps<typeof Layout>;

const PLATFORMS = [
    { key: 'facebook' as const, iconName: 'social-facebook' as const },
    { key: 'instagram' as const, iconName: 'social-instagram' as const },
    { key: 'youtube' as const, iconName: 'social-youtube' as const },
];

export const FooterSocialLinks = ({ media, ...props }: Props) => {
    const { socialLinks } = useContext(AppConfigContext);
    const getLocalizedValue = useGetLocalizedValue();

    if (!socialLinks) return null;

    const links = PLATFORMS.map(({ key, iconName }) => {
        const raw = socialLinks[key];
        const href = raw ? getLocalizedValue(raw) : undefined;
        return isValidSocialHref(href) ? { key, href: href!.trim(), iconName } : null;
    }).filter(Boolean) as {
        key: string;
        href: string;
        iconName: 'social-facebook' | 'social-instagram' | 'social-youtube';
    }[];

    if (links.length === 0) return null;

    return (
        <Layout
            media={{
                initial: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    ...media?.initial,
                },
                ...media,
            }}
            {...props}
        >
            {links.map(({ key, href, iconName }) => (
                <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', width: 24, height: 24 }}
                    data-testid="footer-social-link"
                    data-value={key}
                >
                    <Icon name={iconName} width={24} height={24} />
                </a>
            ))}
        </Layout>
    );
};
